import { useEffect, useState } from "react";

import { GetNotificationBar } from "../../Api/NotificationBar";
import type { InfoCardDTO } from "../../Models/DTOs/InfoCardDTO";

export const NOTIFICATION_BAR_REFRESH_INTERVAL_MS = 60_000;

// Browsers clamp larger delays inconsistently. Re-arm long expiry timers in
// safe chunks so even a card scheduled months ahead expires at the right time.
const MAX_TIMEOUT_MS = 2_147_483_647;

function isCardActiveNow(card: InfoCardDTO, now: number): boolean {
    if (card.isActive === false) return false;

    if (card.startUtc) {
        const start = Date.parse(card.startUtc);
        if (Number.isFinite(start) && start > now) return false;
    }

    if (card.endUtc) {
        const end = Date.parse(card.endUtc);
        if (Number.isFinite(end) && end <= now) return false;
    }

    return true;
}

function useFetchNotificationBar() {
    const [notificationBarData, setNotificationBarData] = useState<InfoCardDTO | null>(null);
    const [notificationBarLoading, setNotificationBarLoading] = useState<boolean>(false);
    const [notificationBarError, setNotificationBarError] = useState<string>("");

    useEffect(() => {
        let isCancelled = false;
        let requestController: AbortController | null = null;
        let refreshTimer: ReturnType<typeof setTimeout> | null = null;
        let expiryTimer: ReturnType<typeof setTimeout> | null = null;
        let fetchInProgress = false;

        const clearRefreshTimer = () => {
            if (refreshTimer !== null) {
                clearTimeout(refreshTimer);
                refreshTimer = null;
            }
        };

        const clearExpiryTimer = () => {
            if (expiryTimer !== null) {
                clearTimeout(expiryTimer);
                expiryTimer = null;
            }
        };

        const scheduleExpiry = (card: InfoCardDTO) => {
            clearExpiryTimer();
            if (!card.endUtc) return;

            const expiresAt = Date.parse(card.endUtc);
            if (!Number.isFinite(expiresAt)) return;

            const expireWhenDue = () => {
                if (isCancelled) return;

                const remaining = expiresAt - Date.now();
                if (remaining <= 0) {
                    expiryTimer = null;
                    setNotificationBarData(null);
                    clearRefreshTimer();
                    void fetchNotificationBar();
                    return;
                }

                expiryTimer = setTimeout(expireWhenDue, Math.min(remaining, MAX_TIMEOUT_MS));
            };

            expireWhenDue();
        };

        const scheduleRefresh = () => {
            clearRefreshTimer();
            refreshTimer = setTimeout(fetchNotificationBar, NOTIFICATION_BAR_REFRESH_INTERVAL_MS);
        };

        const fetchNotificationBar = async () => {
            if (isCancelled || fetchInProgress) return;
            fetchInProgress = true;
            requestController = new AbortController();
            setNotificationBarLoading(true);

            try {
                const data = await GetNotificationBar(requestController);
                if (isCancelled) return;

                const card = data?.item?.infoCard ?? null;
                clearExpiryTimer();

                if (!card || !isCardActiveNow(card, Date.now())) {
                    setNotificationBarData(null);
                } else {
                    setNotificationBarData(card);
                    scheduleExpiry(card);
                }
                setNotificationBarError("");
            } catch (error) {
                if (!isCancelled) {
                    clearExpiryTimer();
                    setNotificationBarData(null);
                    setNotificationBarError("Failed to fetch notification bar data.");
                    console.error("useFetchNotificationBar error:", error);
                }
            } finally {
                fetchInProgress = false;
                if (!isCancelled) {
                    setNotificationBarLoading(false);
                    scheduleRefresh();
                }
            }
        };

        fetchNotificationBar();

        return () => {
            isCancelled = true;
            requestController?.abort();
            clearRefreshTimer();
            clearExpiryTimer();
        };
    }, []);

    return { notificationBarData, notificationBarLoading, notificationBarError };
}

export default useFetchNotificationBar;
